import React, { useState, useEffect } from 'react';
import { formatTime, formatNumber } from '../utils/format';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/axiosConfig';

// Same-origin '/api' only works behind the prod reverse proxy; use the
// configured backend like the rest of the app.
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SystemHealthPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHealthData = async () => {
    try {
      const response = await api.get(`${API_URL}/api/system/health-details`);
      setHealthData(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(true);
      console.error('Health check error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();

    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchHealthData, 5000); // Refresh every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const getStatusColor = (value, thresholds = { warning: 70, danger: 90 }) => {
    if (value >= thresholds.danger) return 'bg-red-500';
    if (value >= thresholds.warning) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusBadge = (status) => {
    if (status === 'healthy' || status === 'connected') {
      return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">{t('superuser.systemHealth.status.healthy')}</span>;
    } else if (status === 'warning') {
      return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">{t('superuser.systemHealth.status.warning')}</span>;
    } else {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">{t('superuser.systemHealth.status.critical')}</span>;
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${formatNumber(bytes / Math.pow(k, i), { maximumFractionDigits: 2 })} ${sizes[i]}`;
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    const parts = [];
    if (days > 0) parts.push(t('superuser.systemHealth.uptime.days', { count: days }));
    if (hours > 0) parts.push(t('superuser.systemHealth.uptime.hours', { count: hours }));
    if (minutes > 0) parts.push(t('superuser.systemHealth.uptime.minutes', { count: minutes }));

    return parts.join(' ') || t('superuser.systemHealth.uptime.minutes', { count: 0 });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{t('superuser.systemHealth.loadError')}</p>
          <button
            onClick={fetchHealthData}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {t('superuser.systemHealth.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/superuser')}
            className="p-2 hover:bg-gray-100 dark:bg-surface-800 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="page-title text-2xl">{t('superuser.systemHealth.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('superuser.systemHealth.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            {t('superuser.systemHealth.autoRefresh')}
          </label>
          <button
            onClick={fetchHealthData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('superuser.systemHealth.refresh')}
          </button>
        </div>
      </div>

      {/* Overall Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              healthData?.overall_status === 'healthy' ? 'bg-green-100' :
              healthData?.overall_status === 'warning' ? 'bg-yellow-100' : 'bg-red-100'
            }`}>
              {healthData?.overall_status === 'healthy' ? (
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {healthData?.overall_status === 'healthy' ? t('superuser.systemHealth.overall.healthy') :
                 healthData?.overall_status === 'warning' ? t('superuser.systemHealth.overall.warning') : t('superuser.systemHealth.overall.critical')}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">{t('superuser.systemHealth.uptimeLabel', { value: formatUptime(healthData?.uptime_seconds || 0) })}</p>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500 dark:text-gray-400">
            {t('superuser.systemHealth.lastUpdated', { time: formatTime(lastUpdated) })}
          </div>
        </div>
      </div>

      {/* System Resources */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* CPU */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('superuser.systemHealth.cpuUsage')}</h3>
            {getStatusBadge(healthData?.cpu?.status)}
          </div>
          <div className="space-y-4">
            <div className="page-title text-3xl">{healthData?.cpu?.usage || 0}%</div>
            <div className="w-full bg-gray-200 dark:bg-surface-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${getStatusColor(healthData?.cpu?.usage || 0)}`}
                style={{ width: `${healthData?.cpu?.usage || 0}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>{t('superuser.systemHealth.cores', { value: healthData?.cpu?.cores || '-' })}</p>
            </div>
          </div>
        </div>

        {/* Memory */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('superuser.systemHealth.memoryUsage')}</h3>
            {getStatusBadge(healthData?.memory?.status)}
          </div>
          <div className="space-y-4">
            <div className="page-title text-3xl">{healthData?.memory?.usage || 0}%</div>
            <div className="w-full bg-gray-200 dark:bg-surface-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${getStatusColor(healthData?.memory?.usage || 0)}`}
                style={{ width: `${healthData?.memory?.usage || 0}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>{t('superuser.systemHealth.used', { value: formatBytes(healthData?.memory?.used || 0) })}</p>
              <p>{t('superuser.systemHealth.total', { value: formatBytes(healthData?.memory?.total || 0) })}</p>
            </div>
          </div>
        </div>

        {/* Disk */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{t('superuser.systemHealth.diskUsage')}</h3>
            {getStatusBadge(healthData?.disk?.status)}
          </div>
          <div className="space-y-4">
            <div className="page-title text-3xl">{healthData?.disk?.usage || 0}%</div>
            <div className="w-full bg-gray-200 dark:bg-surface-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${getStatusColor(healthData?.disk?.usage || 0)}`}
                style={{ width: `${healthData?.disk?.usage || 0}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              <p>{t('superuser.systemHealth.used', { value: formatBytes(healthData?.disk?.used || 0) })}</p>
              <p>{t('superuser.systemHealth.total', { value: formatBytes(healthData?.disk?.total || 0) })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Services Status */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('superuser.systemHealth.services')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Database */}
          <div className="p-4 surface-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  healthData?.services?.database === 'connected' ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="font-medium">{t('superuser.systemHealth.database')}</span>
              </div>
              {getStatusBadge(healthData?.services?.database)}
            </div>
          </div>

          {/* API */}
          <div className="p-4 surface-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  healthData?.services?.api === 'healthy' ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="font-medium">API</span>
              </div>
              {getStatusBadge(healthData?.services?.api)}
            </div>
          </div>

          {/* Cache */}
          <div className="p-4 surface-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  healthData?.services?.cache === 'healthy' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                <span className="font-medium">{t('superuser.systemHealth.cache')}</span>
              </div>
              {getStatusBadge(healthData?.services?.cache || 'healthy')}
            </div>
          </div>

          {/* Storage */}
          <div className="p-4 surface-muted rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  healthData?.services?.storage === 'healthy' ? 'bg-green-500' : 'bg-yellow-500'
                }`}></div>
                <span className="font-medium">{t('superuser.systemHealth.storage')}</span>
              </div>
              {getStatusBadge(healthData?.services?.storage || 'healthy')}
            </div>
          </div>
        </div>
      </div>

      {/* Database Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('superuser.systemHealth.databaseStats')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.totalUsers')}</span>
              <span className="font-semibold">{healthData?.database?.total_users || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.totalCompanies')}</span>
              <span className="font-semibold">{healthData?.database?.total_companies || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.activeSessions')}</span>
              <span className="font-semibold">{healthData?.database?.active_sessions || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.lastBackup')}</span>
              <span className="font-semibold">{healthData?.database?.last_backup || '-'}</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('superuser.systemHealth.apiStats')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.requestsToday')}</span>
              <span className="font-semibold">{healthData?.api?.requests_today || 0}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.avgResponseTime')}</span>
              <span className="font-semibold">{healthData?.api?.avg_response_time || 0} ms</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.errorRate')}</span>
              <span className="font-semibold">{healthData?.api?.error_rate || 0}%</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600 dark:text-gray-400">{t('superuser.systemHealth.activeConnections')}</span>
              <span className="font-semibold">{healthData?.api?.active_connections || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Errors */}
      {healthData?.recent_errors && healthData.recent_errors.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('superuser.systemHealth.recentErrors')}</h3>
          <div className="space-y-3">
            {healthData.recent_errors.map((error, index) => (
              <div key={index} className="p-3 bg-red-50 border border-red-100 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-red-800">{error.message}</p>
                    <p className="text-sm text-red-600">{error.source}</p>
                  </div>
                  <span className="text-xs text-red-500">{error.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemHealthPage;
